'use client';

/// <reference types="@types/google.maps" />

import { Autocomplete } from '@react-google-maps/api';
import { useCallback, useState } from 'react';
import * as formStyles from './styles/formStyles';

// Define type for Autocomplete instance (adjust if needed based on library specifics)
type AutocompleteInstance = google.maps.places.Autocomplete; 

interface AddressFormProps {
  onSubmit: ({ 
    streetName,
    houseNumber, 
    zipCode, 
    city, 
    propertyType,
    latitude,
    longitude
  }: { 
    streetName: string;
    houseNumber: string;
    zipCode: string; 
    city: string; 
    propertyType: 'apartment' | 'house';
    latitude: number;
    longitude: number;
  }) => void;
  dictionary: any;
  disableSubmit?: boolean;
}

export function AddressForm({ onSubmit, dictionary, disableSubmit = false }: AddressFormProps) {
  const [propertyType, setPropertyType] = useState<'apartment' | 'house'>('apartment');
  // State for Autocomplete
  const [autocompleteInstance, setAutocompleteInstance] = useState<AutocompleteInstance | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);
  const [addressInputValue, setAddressInputValue] = useState('');
  
  const [errors, setErrors] = useState<{
    address?: string; // Single error key for address input
    propertyType?: string; 
  }>({});

  // Function to extract address components
  const extractAddressComponents = (place: google.maps.places.PlaceResult): { streetName: string, houseNumber: string, zipCode: string, city: string, latitude: number | null, longitude: number | null } => {
    let streetName = '';
    let houseNumber = '';
    let zipCode = '';
    let city = '';
    const latitude = place.geometry?.location?.lat() ?? null;
    const longitude = place.geometry?.location?.lng() ?? null;

    place.address_components?.forEach(component => {
      const types = component.types;
      if (types.includes('street_number')) {
        houseNumber = component.long_name;
      }
      if (types.includes('route')) {
        streetName = component.long_name;
      }
      if (types.includes('postal_code')) {
        zipCode = component.long_name;
      }
      if (types.includes('locality')) {
        city = component.long_name;
      } else if (types.includes('administrative_area_level_2') && !city) {
        // Fallback for city if locality is not present
        city = component.long_name;
      } else if (types.includes('administrative_area_level_1') && !city) {
        // Further fallback
        city = component.long_name;
      }
    });

    return { streetName, houseNumber, zipCode, city, latitude, longitude };
  };
  
  // --- Autocomplete Handlers ---
  const handleAutocompleteLoad = useCallback((autocomplete: AutocompleteInstance) => {
    setAutocompleteInstance(autocomplete);
    autocomplete.setComponentRestrictions({ country: ['be'] }); // Restrict to Belgium
  }, []);

  const onPlaceSelected = useCallback(() => {
    if (!autocompleteInstance) return;

    const place = autocompleteInstance.getPlace();

    if (!place.geometry?.location || !place.address_components) {
      setErrors({ address: dictionary?.sellingForm?.invalidAddress || 'Please select a valid address from the list.' });
      setSelectedPlace(null);
      setAddressInputValue(''); // Clear input on error
      return;
    }

    const { streetName, houseNumber, zipCode, city, latitude, longitude } = extractAddressComponents(place);
    
    // Validate if a house number was found
    if (!houseNumber) {
      setErrors({ address: dictionary?.sellingForm?.noHouseNumber || 'Please select an address with a house number.' });
      setSelectedPlace(null);
      // Keep the input value so user can see what they selected, but it's invalid
      setAddressInputValue(place.formatted_address || ''); 
      return;
    }

    // --- Valid address selected ---
    setSelectedPlace(place); // Store the selected place object
    setAddressInputValue(place.formatted_address || ''); // Update input field
    setErrors({}); // Clear errors
    
  }, [autocompleteInstance, dictionary]);

  const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddressInputValue(e.target.value);
    // If user types manually, invalidate the selection
    if (selectedPlace) {
      setSelectedPlace(null);
      setErrors({ address: dictionary?.sellingForm?.selectFromList || 'Please select a valid address from the list.' });
    }
    // Clear specific address error if they start typing again
    if (errors.address) {
      setErrors(prev => ({ ...prev, address: undefined }));
    }
  };
  // --- End Autocomplete Handlers ---

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newErrors: typeof errors = {};

    // Validate that a place was selected
    if (!selectedPlace) {
      newErrors.address = dictionary?.sellingForm?.selectFromList || 'Please select a valid address from the list.';
      setErrors(newErrors);
      return;
    }
    
    // Extract data from the selected place *again* to be sure
    const { streetName, houseNumber, zipCode, city, latitude, longitude } = extractAddressComponents(selectedPlace);
    
    // Double check extraction results (especially lat/lng)
    if (!latitude || !longitude) {
      newErrors.address = dictionary?.sellingForm?.invalidAddress || 'Please select a valid address from the list.';
      setErrors(newErrors);
      return;
    }

    // Validate property type (selected via radio buttons below)
    const formData = new FormData(e.currentTarget);
    const propertyTypeValue = formData.get('propertyType') as 'apartment' | 'house'; // Get type from form data
    if (!propertyTypeValue) {
      newErrors.propertyType = dictionary?.sellingForm?.requiredField || 'Property type is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSubmit({ 
      streetName,
      houseNumber, 
      zipCode, 
      city, 
      propertyType: propertyTypeValue, // Use value from form data
      latitude,
      longitude
    });
  };

  // Error message handling remains similar
  const errorMessages = {
    requiredField: {
      en: 'This field is required',
      nl: 'Dit veld is verplicht',
      fr: 'Ce champ est obligatoire'
    }
    // Add other specific error messages if needed
  };

  const getErrorMessage = (key: keyof typeof errorMessages) => {
    return dictionary?.sellingForm?.[key] || 
           errorMessages[key].en;
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="w-full">
        <p className="text-base text-zinc-800 mb-2">
          {/* Update instruction text */}
          {dictionary?.sellingForm?.addressAutocompleteMessage || 'Enter the address of the property:'}
        </p>
        
        {/* --- Autocomplete Input --- */}
        <div className="mb-2"> 
          <Autocomplete
            onLoad={handleAutocompleteLoad}
            onPlaceChanged={onPlaceSelected}
            options={{
              // fields: ["address_components", "geometry", "formatted_address"], // Specify fields needed
              types: ['address'], // Suggest only specific addresses
            }}
          >
            <input
              type="text"
              name="address" // Keep name for potential form data access if needed elsewhere
              placeholder={dictionary?.sellingForm?.addressPlaceholder || 'Start typing address...'}
              className={`${formStyles.inputElementBaseClasses} w-full`} // Ensure full width
              value={addressInputValue}
              onChange={handleAddressInputChange}
              // Prevent form submission on Enter key within Autocomplete
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} 
            />
          </Autocomplete>
          {/* Display Address Error */}
          {errors.address && <p className={`${formStyles.errorMessageClasses} mt-1`}>{errors.address}</p>}
        </div>
        {/* --- End Autocomplete Input --- */}

        {/* --- Conditional Rendering for Property Type & Submit --- */}
        {/* Show property type selection only after a valid address is selected */}
        {selectedPlace && (
          <>
            <div className="mt-4"> {/* Add some margin top */}
              <p className="text-base text-zinc-800 mb-2">
                {dictionary?.sellingForm?.propertyTypeQuestion || 'What type of property is it?'}
              </p>
              <div className="flex items-center gap-4">
                <label htmlFor="propertyTypeApartment" className="flex items-center cursor-pointer p-2 border rounded-lg hover:bg-gray-50 transition-colors has-[:checked]:border-zinc-900 bg-white/50 has-[:checked]:bg-white">
                  <input
                    type="radio"
                    id="propertyTypeApartment"
                    name="propertyType" // Ensure name matches for FormData access
                    value="apartment"
                    checked={propertyType === 'apartment'}
                    onChange={() => setPropertyType('apartment')}
                    className="h-4 w-4 border-gray-300 text-zinc-900 focus:ring-zinc-900 accent-zinc-900"
                  />
                  <span className="ml-2 text-base text-zinc-800">
                    {dictionary?.sellingForm?.propertyTypeApartment || 'Apartment'}
                  </span>
                </label>

                <label htmlFor="propertyTypeHouse" className="flex items-center cursor-pointer p-2 border rounded-lg hover:bg-gray-50 transition-colors has-[:checked]:border-zinc-900 bg-white/50 has-[:checked]:bg-white">
                  <input
                    type="radio"
                    id="propertyTypeHouse"
                    name="propertyType" // Ensure name matches for FormData access
                    value="house"
                    checked={propertyType === 'house'}
                    onChange={() => setPropertyType('house')}
                    className="h-4 w-4 border-gray-300 text-zinc-900 focus:ring-zinc-900 accent-zinc-900"
                  />
                  <span className="ml-2 text-base text-zinc-800">
                    {dictionary?.sellingForm?.propertyTypeHouse || 'House'}
                  </span>
                </label>
              </div>
              {errors.propertyType && <p className={`${formStyles.errorMessageClasses} mt-1`}>{errors.propertyType}</p>}
            </div>

            <div className="mt-2">
              <button
                type="submit"
                // Disable submit if original disableSubmit is true OR if autocomplete instance isn't ready (optional check)
                disabled={disableSubmit || !autocompleteInstance} 
                className={`${formStyles.submitButtonBaseClasses} ${
                  (disableSubmit || !autocompleteInstance) ? formStyles.submitButtonDisabledClasses : formStyles.submitButtonHoverClasses
                }`}
              >
                {dictionary?.sellingForm?.continueButton || 'Continue'}
              </button>
            </div>
          </>
        )}
        {/* --- End Conditional Rendering --- */}
      </form>

      {/* Removed old style tag as it's likely not needed */}
    </>
  );
} 